import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown } from "lucide-react";
import axios from "axios";
import { API, pageVariants } from "../components/app/constants";

export const SuccessPage = ({ user, onUserUpdate, onNavigate }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      onNavigate("/pricing");
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          // Refresh user data
          if (user) {
            const userRes = await axios.get(`${API}/users/${user.id}`);
            onUserUpdate(userRes.data);
          }
        } else if (response.data.status === "expired") {
          setStatus("expired");
        } else {
          attempts++;
          setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        attempts++;
        setTimeout(pollStatus, 2000);
      }
    };

    pollStatus();
  }, [sessionId, user, onUserUpdate, onNavigate]);

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center"
    >
      <div className="max-w-md mx-auto text-center">
        {status === "checking" && (
          <>
            <div className="spinner mx-auto mb-8" />
            <h1 className="font-serif text-3xl mb-4">Processing Payment...</h1>
            <p className="text-white/60">Please wait while we confirm your purchase.</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]">
              <Check className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif text-4xl mb-4" data-testid="success-title">Payment Successful!</h1>
            <p className="text-white/60 mb-8">
              Your credits have been added to your account.
            </p>
            <button 
              onClick={() => onNavigate("/create")}
              className="btn-primary"
              data-testid="start-creating-btn"
            >
              Start Creating
            </button>
          </>
        )}
        
        {(status === "expired" || status === "timeout") && (
          <>
            <h1 className="font-serif text-3xl mb-4">Something Went Wrong</h1>
            <p className="text-white/60 mb-8">
              Your payment could not be processed. Please try again.
            </p>
            <button 
              onClick={() => onNavigate("/pricing")}
              className="btn-secondary"
            >
              Back to Pricing
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};

// Subscription Success Page
export const SubscriptionSuccessPage = ({ user, onUserUpdate, onNavigate }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    
    let attempts = 0;
    const pollStatus = async () => {
      if (attempts > 15) { setStatus("timeout"); return; }
      try {
        const res = await axios.get(`${API}/subscriptions/verify/${sessionId}`);
        if (res.data.status === "activated" || res.data.status === "already_activated") {
          setStatus("success");
          if (res.data.user) onUserUpdate(res.data.user);
        } else if (res.data.status === "pending") {
          attempts++;
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("error");
        }
      } catch (err) {
        attempts++;
        setTimeout(pollStatus, 2000);
      }
    };
    pollStatus();
  }, [sessionId, onUserUpdate]);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center">
        {status === "checking" && (
          <>
            <div className="spinner mx-auto mb-8" />
            <h1 className="font-serif text-3xl mb-4">Activating Subscription...</h1>
            <p className="text-white/60">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif text-4xl mb-4" data-testid="sub-success-title">Welcome to Premium!</h1>
            <p className="text-white/60 mb-4">You now have unlimited portrait generations with no watermarks.</p>
            <div className="bg-white/5 border border-[#D4AF37]/20 rounded-xl p-4 mb-8 text-left space-y-2">
              {["Unlimited generations", "No watermarks on any portrait", "Permanent Vault storage", "All 12+ styles included", "Priority generation"].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white/80">{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => onNavigate("/create")} className="btn-primary" data-testid="start-creating-btn">
              Start Creating — Unlimited
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <h1 className="font-serif text-3xl mb-4">Something Went Wrong</h1>
            <p className="text-white/60 mb-8">We couldn't verify your subscription. Please contact support.</p>
            <button onClick={() => onNavigate("/pricing")} className="btn-secondary">Back to Pricing</button>
          </>
        )}
      </div>
    </motion.div>
  );
};


